<template>
  <div class="d-inline">
    <b-button
      @click="showModal"
      size="sm"
      :variant="isCompanyNameDefined ? 'outline-primary' : 'primary'"
      :class="isCompanyNameDefined ? '' : 'mr-2'"
    >
      <i v-if="!isCompanyNameDefined" class="fa fa-plus" aria-hidden="true"></i>
      {{ title }}
    </b-button>

    <b-modal
      size="xl"
      :id="modalId"
      :ref="modalId"
      @ok="saveRecord"
      @show="onResetModal"
      :ok-title=" isSaving ? 'Saving...' : 'Save'"
      :ok-disabled="isSaving"
    >
      <template slot="modal-header">
        <h5 class="modal-title">{{ title }}</h5>
      </template>
      <div>
        <form>
            <b-row>
                <b-col>
                <div class="d-flex justify-content-start align-items-end mb-2">
                    <b-form-group label="Purchase Order Number" class="mr-2">
                        <b-form-input v-model="poFilters.poNumber" @input="onSearch" size="sm" />
                    </b-form-group>
                    <b-form-group label="Company" class="mr-2">
                        <b-form-input v-model="poFilters.companyName"  @input="onSearch" size="sm" />
                    </b-form-group>
                    <b-form-group label="Sales Rep" class="mr-2">
                        <b-form-input v-model="poFilters.salesRep" size="sm" @input="onSearch" />
                    </b-form-group>
                    <date-range-picker :dateFrom="poFilters.dateFrom" :dateTo="poFilters.dateTo" @handleSearch="onSearch"></date-range-picker>
                    <b-button class="mr-2" size="sm" @click="clearFilter">Clear</b-button>
                </div>
                </b-col>
            </b-row>
            <h5>
              Purchase Orders
              <i v-show="isLoadingPO" class="fa fa-spinner fa-pulse fa-fw"></i>
            </h5>
            <b-row>
                <b-col>
                <div class="grid-container mb-2">
                    <grid-component
                      isResizable
                      isReorderable
                      :itemData="itemData"
                      :fields="fields"
                      :gridId="gridId"
                      :handleRowClick="onRowClick"
                     />
                </div>
                </b-col>
            </b-row>
            <h5>
              Purchase Order Lines
              <i v-show="isLoadingLines" class="fa fa-spinner fa-pulse fa-fw"></i>
            </h5>
            <b-row>
                <b-col>
                    <div class="grid-container">
                      <grid-component
                        isResizable
                        isReorderable
                        :gridId="gridLineId"
                        :itemData="itemLineData"
                        :fields="lineFields"
                        :handleRowClick="onRowLineClick"
                       />
                    </div>
                </b-col>
            </b-row>
            <h5>
              Inventory Used
              <i v-show="isLoadingInventory" class="fa fa-spinner fa-pulse fa-fw"></i>
            </h5>
            <b-row>
              <b-col>
                  <div class="grid-container">
                      <h6>Select an inventory returned and input quantity returned, reason of return and comment if necessary.</h6>
                      <grid-component
                        isResizable
                        isReorderable
                        :gridId="gridInventoryId"
                        :itemData="inventoryItemData"
                        :fields="inventoryField"
                        :optionsField="rmaReasons"
                        :checkBoxValues="checkedInventoryReturnedIds"
                        :autoCheckBy="'qtyReturning'"
                        @checkedItem="checkedInventoryReturnedIds=$event"
                       />
                  </div>
              </b-col>
            </b-row>
        </form>
      </div>
      <template slot="modal-footer">
        <b-form-group label="Comments" class="mr-auto">
          <b-form-input v-model="vrmaValues.comments" />
        </b-form-group>
        <b-form-group label="RMA Terms" class="mr-2 form-inline">
          <b-form-select v-model="vrmaValues.actionId" class="mr-2">
              <option v-for="option in sortSelect(vendorRmaActions)" :key="option.name" :value="option.id">
                  {{option.name }}</option>
          </b-form-select>
          <b-form-select v-model="vrmaValues.termsId">
              <option v-for="option in sortSelect(vendorRmaTerms)" :key="option.name" :value="option.id">
                  {{option.name }}</option>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Return To" class="mr-2">
            <b-form-select v-model="vrmaValues.returnTo">
                <option v-for="option in returnToOptions" :key="option" :value="option">
                    {{option }}</option>
            </b-form-select>
        </b-form-group>

        <b-button @click="$bvModal.hide(modalId)">Cancel</b-button>
        <b-button variant="primary" @click="saveRecord()" :disabled="isSaving"><i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save</b-button>

      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import debounce from 'lodash/debounce';
import VueSimpleSuggest from 'vue-simple-suggest';
import DateRangePicker from '@/shared/components/DateRangePicker/IndexNew';
import GridSettings from '@/shared/components/GridSettings';
import GridComponent from '@/shared/components/GridComponent';
import { required } from 'vuelidate/lib/validators';
import { date } from '@/shared/filters';
import { GRID_ID, MODAL_ID, SAVE_SETTINGS_DELAY, PAGE_SIZE, REQUEST_DATE, SEARCH_DEBOUNCE_TIME } from '@/shared/constants';
import poFields from '@/pages/PurchaseOrders/fields';
import poLineFields from '@/pages/PurchaseOrders/components/PurchaseOrderLines/fields';
import inventoryUsedField from '@/pages/Customerrmas/components/add/inventoryUsedField';

export default {
  components: {
    GridSettings,
    VueSimpleSuggest,
    DateRangePicker,
    GridComponent
  },
  props: {
    companyName: [String],
    poNumber: [Number, String],
    rmaLineId: [Number, String],
  },
  data() {
    return {
      date,
      fields: poFields,
      lineFields: poLineFields,
      inventoryField: inventoryUsedField,
      requestDate: REQUEST_DATE,
      checkedRowItemIds: [],
      checkedInventoryReturnedIds: [],
      modalId: 'add-vendor-rma',
      title: 'Create Vendor RMA',
      poFilters: {},
      selectedRowItemId: 0,
      selectedRowLineId: 0,
      isLoadingPO: false,
      isLoadingLines: false,
      isLoadingInventory: false,
      unHideLineFields: [
          'qtyReturned', 'reason', 'comment'
      ],
      linesReturnedValues: {},
      vrmaValues: {},
      enableLineValues: false,
      poLinesSelected: {},
      requiredFields: [
        'quantityReturning', 'rmaReasonId', 'rmaTermsId'
      ],
      isSaving: false,
      filterPageName: 'addVrmaModal'
    }
  },
  validations: {
    filterValObj: {
      companyId: { required },
    },
  },
  filters: {
    date,
  },
  computed: {
    ...mapGetters('account', ['gridSettingsById']),
    ...mapGetters('lookup', ['rmaReasons', 'vendorRmaTerms', 'vendorRmaActions']),
    ...mapGetters('inventory', ['inventoryListFiltered']),
    ...mapGetters('purchaseorders', [ 'gridList' ]),
    // ...mapGetters('purchaseOrderLines', ['poLinesForRma', 'poLineWithInventory']),
    ...mapGetters('purchaseOrderLines', ['poLinesForRma']),
    gridId: function() {
      return GRID_ID.PURCHASE_ORDERS;
    },
    gridLineId: function() {
      return GRID_ID.PURCHASE_ORDER_LINES
    },
    gridInventoryId: function() {
      return GRID_ID.RMA_INVENTORY;
    },
    gridSettings() {
      return this.gridSettingsById(this.gridId);
    },
    disableAdd() {
      return this.filterValObj.companyId == 0;
    },
    itemData() {
		  return this.gridList.map((purchaseorder) => ({ ...purchaseorder, selected: purchaseorder.id === this.selectedRowItemId }));
    },
    itemLineData() {
      return this.poLinesForRma.map((lines) => ({ ...lines, selected: lines.id == this.selectedRowLineId }));
    },
    inventoryItemData() {
      if(this.inventoryListFiltered.length === 0) return [];

      return this.inventoryListFiltered.map((inventory) => ({
          ...inventory,
          quantityPulled: this.poLinesSelected.qtyReceived,
          qtyReturning: 0,
          rmaReasonId: 0,
          comments: null,
          selected: false
       }));
    },
    returnToOptions() {
      return [...new Set(this.sortSelect(this.itemData,'companyName').map(x => x.companyName))];
    },
    isSaveEnable() {
      return this.validateRequiredFields();
    },
    isCompanyNameDefined() {
      return this.companyName != undefined;
    },
    isPrepopulated() {
      return this.isCompanyNameDefined || this.poNumber > 0;
    }
  },
  methods: {
    ...mapActions('account', ['saveGridSettings']),
    ...mapActions('purchaseorders', [ 'search','clearFilters']),
    // ...mapActions('purchaseOrderLines', [ 'getPOLinesForRma', 'getPOLineWithInventory' ]),
    ...mapActions('purchaseOrderLines', [ 'getPOLinesForRma']),
    ...mapActions('vendorrmas', [ 'onSaveVRMAs' ]),
    ...mapActions('inventory', ['getInventoryFiltered']),
    ...mapMutations('purchaseOrderLines', [ 'setPOLinesForRma']),
    ...mapMutations('purchaseorders', ['setItem']),
    // ...mapMutations('inventory', ['setInventoryListFiltered']),
    ...mapMutations('inventory', {inventorySetItem: 'setItem'}),
    onResetModal() {
      this.checkedRowItemIds = [];
      this.resetModal();
    },
    showModal() {
      if (this.isPrepopulated) {
        this.setPrepoulatedFilters();
      }
      this.$bvModal.show(this.modalId);
    },
    async onRowClick(row) {
      this.selectedRowItemId = row.dataItem.id;
      this.clearLineValues();
      if (this.selectedRowItemId) {
        try {
          this.isLoadingLines = true;
          await this.getPOLinesForRma(this.selectedRowItemId);

          if (this.itemLineData.length == 1) {
            this.onRowLineClick({dataItem: {id: this.itemLineData[0].id}});
          }
          this.isLoadingLines = false;
        }catch (e) {
          this.isLoadingLines = false;
        }
      }
    },
    async onRowLineClick(row) {
      this.selectedRowLineId = row.dataItem.id;
      this.poLinesSelected = this.itemLineData.find(item => item.id == this.selectedRowLineId);
      try {
        this.isLoadingInventory = true;
        await this.getInventoryFiltered({
          purchaseOrderLineId: this.selectedRowLineId
        });

        this.isLoadingInventory = false;
      }catch (e) {
        this.isLoadingInventory = false;
      }
    },
    clearLineValues() {
      this.selectedRowLineId = 0;
      this.setPOLinesForRma([]);
      this.poLinesSelected = {};
      // this.setInventoryListFiltered([]);
      this.inventorySetItem({ resource:'inventoryListFiltered', payload: [] });
    },
    onSearch: debounce(function(data=false) {
      if(typeof data === "object") { Object.assign(this.poFilters,data) }
      try {
        this.isLoadingPO = true;
        this.search({'filters': this.poFilters, page: this.filterPageName});
        this.clearLineValues();
        this.isLoadingPO = false;
      }catch (e) {
        this.isLoadingPO = false;
      }
		}, SEARCH_DEBOUNCE_TIME),
      clearFilter(callSearch = true) {
          this.clearObject(this.poFilters);
          if (callSearch) {
              this.search({'filters':this.poFilters,page:this.filterPageName});
          }
      },
    getInventoryReturned() {
      return this.inventoryItemData.filter((item) => {
        return this.checkedInventoryReturnedIds.some((id) => {
          return id == item.id;
        })
      }).map(items => ({
        inventoryId: items.id,
        qtyReturning: items.qtyReturning,
        reasonId: items.rmaReasonId,
        comments: items.comments
      }));
    },

    async saveRecord() {
      if (!this.isValidfields()) {
        this.$snotify.info('Processing V-RMA', 'Vendor RMA');
        console.log(this.rmaLineId, 'RMA Line Id')
        try {
          this.isSaving = true;
          await this.onSaveVRMAs({
            ...this.vrmaValues,
            companyId: this.poLinesSelected.companyId,
            contactId: this.poLinesSelected.contactId,
            purchaseOrderId: this.selectedRowItemId,
            purchaseOrderLineId: this.selectedRowLineId,
            rmaLineId: this.rmaLineId != undefined ? this.rmaLineId : 0,
            items: this.getInventoryReturned(),
          }).then((res) => {
            this.clearFilter(false);
            this.isSaving = false;
            this.$bvModal.hide(this.modalId);
            this.$snotify.success('Created Vendor RMA', 'Vendor RMA');
            this.$router.push({
              name: 'vendorrmas.edit',
              params: {id: res.id},
            });
          });
        }catch (e) {
          this.isSaving = false;
        }
      }
    },
    isValidfields() {
      const h = this.$createElement
      let isValid = false;
      let errors = [];

      if (this.selectedRowItemId == 0) {
        errors.push( h('li', {}, 'You must select an purchase order to proceed in creating Vendor RMA'));
      }

      // if (this.checkedInventoryReturnedIds.length == 0) {
      //   errors.push( h('li', {}, 'You must select an inventory used'));
      // }

      let validationMsg = h('ul', {}, errors);

      if(isValid = errors.length > 0){
        this.openAlertBox({message: validationMsg,title: 'Please correct the following'});
      }

      return isValid;
    },
    onResetModal() {

    },
    validateRequiredFields() {
      for (let i = 0; i < this.requiredFields.length; i++) {
        let field = this.requiredFields[i];
        if (this.linesReturnedValues[field] == undefined || this.linesReturnedValues[field] == null) {
          return false;
          break;
        }
      }

      return true;
    },
    generateLineReturnFields() {
      return {
        quantityReturning: null,
        rmaReasonId: null,
        rmaComments: null
      }
    },
    setPrepoulatedFilters() {
      let onSearch = false;
      this.clearFilters(this.filterPageName);
      if (this.isCompanyNameDefined) {
        onSearch = true;
        this.poFilters.companyName = this.companyName;
      }

      if (this.poNumber > 0) {
        onSearch = true;
        this.poFilters.poNumber = this.poNumber;
      }

      if (onSearch) {
        this.onSearch();
      }
    }
  },
  watch: {
    companyName() {
      if (this.isCompanyNameDefined) {
        this.setPrepoulatedFilters();
      }
    },
    poNumber() {
      this.setPrepoulatedFilters();
    }
  },
  async mounted() {
    this.setItem({ resource:'filterPage', payload: this.filterPageName });
    this.clearFilters(this.filterPageName);
    this.clearLineValues();
    if (!this.isCompanyNameDefined && this.poNumber == undefined)
        this.setItem({ resource:'gridList', payload: [] });
  }
}
</script>
